// SPDX-License-Identifier: BUSL-1.1

/**
 * API Keys management page.
 *
 * Allows super admins to create, view, and revoke API keys.
 * The full key is shown exactly once on creation — never stored in plaintext.
 *
 * Form pattern: react-hook-form + zod for the create-key form (per ui-002).
 * Dialog open/close, the revealed-key banner, and the revoke-confirm target
 * stay as plain `useState` — those are UI state, not form state.
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { API_BASE } from '@/lib/api-client';
import { showSuccess, showError } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, Copy, KeyIcon, PlusIcon, RefreshCw, Trash2 } from 'lucide-react';

interface ApiKeyRow {
  id: string;
  name: string;
  role: string;
  actorId: string;
  actorType: string;
  organizationId: string | null;
  createdAt: string;
  revokedAt: string | null;
}

interface CreateKeyResponse {
  id: string;
  key: string;
  name: string;
  role: string;
}

const createKeySchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  actorType: z.enum(['agent', 'human', 'system']),
  role: z.enum(['admin', 'viewer', 'reader']),
});

type CreateKeyValues = z.infer<typeof createKeySchema>;

async function fetchKeys(token: string | null): Promise<ApiKeyRow[]> {
  const res = await fetch(`${API_BASE}/keys`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error ?? 'Failed to load API keys');
  }
  return res.json();
}

async function createKey(
  token: string | null,
  name: string,
  actorId: string,
  actorType: string,
  role: string,
): Promise<CreateKeyResponse> {
  const res = await fetch(`${API_BASE}/keys`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    body: JSON.stringify({ name, actorId, actorType, role }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error ?? 'Failed to create key');
  }
  return data;
}

async function revokeKey(token: string | null, keyId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/keys/${keyId}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error ?? 'Failed to revoke key');
  }
}

export function KeysPage() {
  const { token, token: rawToken } = useAuth();
  const qc = useQueryClient();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [revealedKey, setRevealedKey] = useState<{ name: string; fullKey: string } | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyRow | null>(null);
  const [createError, setCreateError] = useState('');

  const form = useForm<CreateKeyValues>({
    resolver: zodResolver(createKeySchema),
    defaultValues: {
      name: '',
      actorType: 'agent',
      role: 'admin',
    },
  });

  const { data: allKeys = [], isLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => fetchKeys(token),
  });

  // Only show active (non-revoked) keys
  const activeKeys = allKeys.filter((k) => !k.revokedAt);

  const createMutation = useMutation({
    mutationFn: (values: CreateKeyValues) =>
      createKey(token, values.name.trim(), rawToken ?? 'unknown', values.actorType, values.role),
    onSuccess: (data) => {
      showSuccess('API key created');
      setShowCreateDialog(false);
      form.reset();
      setCreateError('');
      setRevealedKey({ name: data.name, fullKey: data.key });
      qc.invalidateQueries({ queryKey: ['api-keys'] });
    },
    onError: (err: Error) => {
      setCreateError(err.message);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (keyId: string) => revokeKey(token, keyId),
    onSuccess: () => {
      setRevokeTarget(null);
      showSuccess('API key revoked');
      qc.invalidateQueries({ queryKey: ['api-keys'] });
    },
    onError: (err: Error) => {
      showError('Failed to revoke key', err.message);
    },
  });

  async function copyKey(text: string) {
    if (typeof window === 'undefined' || !window.navigator?.clipboard) {
      showError('Copy failed', 'Clipboard API not available');
      return;
    }
    await window.navigator.clipboard.writeText(text);
    showSuccess('Copied to clipboard');
  }

  function handleDialogChange(open: boolean) {
    setShowCreateDialog(open);
    if (!open) {
      form.reset();
      setCreateError('');
    }
  }

  return (
    <div className="container mx-auto max-w-4xl py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-100">API Keys</h1>
          <p className="text-sm text-neutral-500">
            Manage API keys for CLI and programmatic access
          </p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={handleDialogChange}>
          <DialogTrigger
            render={
              <Button className="bg-emerald-600 hover:bg-emerald-500">
                <PlusIcon className="mr-2 h-4 w-4" />
                Create key
              </Button>
            }
          />
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create API Key</DialogTitle>
              <DialogDescription>
                Give your key a name to remember what it is for. Only super admins can manage keys.
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit((values) => createMutation.mutate(values))}
                className="flex flex-col gap-4"
                id="create-key-form"
              >
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. CI/CD Pipeline" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="actorType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>For</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="agent">Agent / Service</SelectItem>
                          <SelectItem value="human">Human User</SelectItem>
                          <SelectItem value="system">System</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Role</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="admin">Admin — full access</SelectItem>
                          <SelectItem value="viewer">Viewer — read-only</SelectItem>
                          <SelectItem value="reader">Reader — flags/configs only</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {createError && <p className="text-sm text-red-400">{createError}</p>}
              </form>
            </Form>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => handleDialogChange(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                form="create-key-form"
                className="bg-emerald-600 hover:bg-emerald-500"
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating...' : 'Create key'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Revealed key banner */}
      {revealedKey && (
        <div className="mb-6 rounded-lg border border-amber-700 bg-amber-950 p-4">
          <div className="mb-2 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-400" />
            <p className="font-semibold text-amber-100">
              Save your API key — it will not be shown again!
            </p>
          </div>
          <div className="mb-3 flex items-center gap-2 rounded bg-neutral-900 px-3 py-2">
            <code className="flex-1 font-mono text-sm break-all text-neutral-100">
              {revealedKey.fullKey}
            </code>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => copyKey(revealedKey.fullKey)}
              className="shrink-0 text-amber-300 hover:text-amber-200"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <Button onClick={() => setRevealedKey(null)}>I have saved this key</Button>
        </div>
      )}

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <RefreshCw className="h-6 w-6 animate-spin text-neutral-500" />
        </div>
      ) : activeKeys.length === 0 ? (
        <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-12 text-center">
          <KeyIcon className="mx-auto mb-4 h-12 w-12 text-neutral-700" />
          <h2 className="mb-2 text-lg font-medium text-neutral-300">No API keys yet</h2>
          <p className="mb-6 text-sm text-neutral-500">
            Create your first API key to start using the CLI.
          </p>
          <Button
            className="bg-emerald-600 hover:bg-emerald-500"
            onClick={() => setShowCreateDialog(true)}
          >
            <PlusIcon className="mr-2 h-4 w-4" />
            Create your first key
          </Button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-neutral-800">
          <Table>
            <TableHeader>
              <TableRow className="border-neutral-800">
                <TableHead className="text-neutral-400">Name</TableHead>
                <TableHead className="text-neutral-400">For</TableHead>
                <TableHead className="text-neutral-400">Role</TableHead>
                <TableHead className="text-neutral-400">Organization</TableHead>
                <TableHead className="text-neutral-400">Created</TableHead>
                <TableHead className="w-[80px] text-neutral-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activeKeys.map((key) => (
                <TableRow key={key.id} className="border-neutral-800">
                  <TableCell className="font-medium text-neutral-200">{key.name}</TableCell>
                  <TableCell className="text-sm text-neutral-400">{key.actorType}</TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${
                        key.role === 'admin'
                          ? 'bg-blue-900/50 text-blue-300'
                          : key.role === 'viewer'
                            ? 'bg-neutral-800 text-neutral-300'
                            : 'bg-neutral-800 text-neutral-400'
                      }`}
                    >
                      {key.role}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-neutral-500">
                    {key.organizationId ? (
                      <code className="font-mono text-xs text-neutral-400">
                        {key.organizationId}
                      </code>
                    ) : (
                      <span className="text-neutral-600">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-neutral-500">
                    {new Date(key.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-400 hover:text-red-300"
                      onClick={() => setRevokeTarget(key)}
                      title="Revoke key"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Revoke confirmation dialog */}
      <Dialog open={!!revokeTarget} onOpenChange={() => setRevokeTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke API key</DialogTitle>
            <DialogDescription>
              Are you sure you want to revoke <strong>{revokeTarget?.name}</strong>? This action
              cannot be undone. Any systems using this key will lose access immediately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={revokeMutation.isPending}
              onClick={() => revokeTarget && revokeMutation.mutate(revokeTarget.id)}
            >
              {revokeMutation.isPending ? 'Revoking...' : 'Revoke key'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
